import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { AssetStatusBadge } from "../components/ui/index.jsx";
import { adminGetAsset, adminUpdateAsset, adminDeleteAsset } from "../services/assetService.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const ICONS = {
  SERVER:"bi-hdd-rack", COMPUTER:"bi-pc-display-horizontal",
  WORKSTATION:"bi-pc-display-horizontal", PRINTER:"bi-printer",
  ROUTER:"bi-router", SWITCH:"bi-diagram-3", FIREWALL:"bi-shield-lock",
  ACCESS_POINT:"bi-wifi", NETWORK:"bi-router", OTHER:"bi-cpu",
};
const ICON_COLORS = {
  SERVER:"var(--accent)", COMPUTER:"var(--accent3)", WORKSTATION:"var(--accent3)",
  PRINTER:"var(--warning)", ROUTER:"var(--success)", SWITCH:"var(--success)",
  FIREWALL:"var(--danger)", ACCESS_POINT:"var(--accent3)", NETWORK:"var(--success)",
  OTHER:"var(--text-3)",
};
const TYPE_LABELS = {
  SERVER:"Serveur", COMPUTER:"Poste de travail", WORKSTATION:"Poste de travail",
  PRINTER:"Imprimante", ROUTER:"Routeur", SWITCH:"Switch",
  FIREWALL:"Pare-feu", ACCESS_POINT:"Borne Wi-Fi", NETWORK:"Réseau", OTHER:"Autre",
};

function formatRelative(iso) {
  if (!iso) return "inconnu";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `il y a ${diff} sec`;
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function AssetDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [asset,   setAsset]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [saving,  setSaving]  = useState(false);

  // Formulaire d'édition inline
  const [edit,    setEdit]    = useState(false);
  const [form,    setForm]    = useState({});

  useEffect(() => { fetchAsset(); }, [id]);

  const fetchAsset = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminGetAsset(id);
      const data = res?.data ?? res;
      setAsset(data);
      setForm({
        location: data.location || "",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Statut géré par Atera — lecture seule, pas de modification manuelle
  // eslint-disable-next-line no-unused-vars
  const handleStatusChange = () => {};

  // Sauvegarde uniquement l'emplacement — tout le reste est géré par Atera
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateAsset(id, {
        location: form.location || null,
      });
      setAsset(res?.data ?? res);
      setEdit(false);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Suppression
  const handleDelete = async () => {
    if (!window.confirm("Supprimer cet équipement définitivement ?")) return;
    try {
      await adminDeleteAsset(id);
      navigate("/assets");
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── États intermédiaires ─────────────────────
  if (loading) return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body" style={{ textAlign:"center", padding:60, color:"var(--text-3)" }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize:24, display:"block", marginBottom:8 }}/>
        Chargement…
      </div>
    </>
  );

  if (error || !asset) return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body">
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:14, color:"var(--danger)", display:"flex", alignItems:"center", gap:12 }}>
          <i className="bi bi-exclamation-triangle"/>{error || "Équipement introuvable."}
          <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={() => navigate("/assets")}>
            <i className="bi bi-arrow-left me-1"/>Retour
          </button>
        </div>
      </div>
    </>
  );

  const icon      = ICONS[asset.type]      || "bi-cpu";
  const iconColor = ICON_COLORS[asset.type] || "var(--text-3)";
  const diskPct   = asset.diskTotalGb && asset.diskUsedGb
    ? Math.round((asset.diskUsedGb / asset.diskTotalGb) * 100) : null;

  return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body">

        {/* Breadcrumb */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, fontSize:13, color:"var(--text-3)" }}>
          <span style={{ cursor:"pointer", color:"var(--accent)" }} onClick={() => navigate("/assets")}>
            <i className="bi bi-arrow-left me-1"/>Parc informatique
          </span>
          <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
          <span style={{ color:"var(--text-2)" }}>{asset.hostname || "Équipement"}</span>
          {asset.company && (
            <>
              <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
              <span style={{ color:"var(--accent)" }}>{asset.company.name}</span>
            </>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:20, alignItems:"start" }}>

          {/* ── Colonne principale ── */}
          <div>

            {/* En-tête */}
            <div className="itm-card anim-fade-up" style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16 }}>
                <div style={{ width:56, height:56, borderRadius:14, background:"var(--navy-soft)", border:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, color:iconColor, flexShrink:0 }}>
                  <i className={`bi ${icon}`}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"Syne,sans-serif", fontSize:20, fontWeight:800, color:"var(--text-1)", marginBottom:4 }}>
                    {asset.hostname || "—"}
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                    <AssetStatusBadge status={asset.status}/>
                    <span style={{ fontSize:12, color:"var(--text-3)" }}>
                      {TYPE_LABELS[asset.type] || asset.type}
                    </span>
                    {asset.company && (
                      <span style={{ fontSize:12, color:"var(--accent)" }}>
                        <i className="bi bi-building me-1"/>{asset.company.name}
                      </span>
                    )}
                    {saving && <i className="bi bi-arrow-repeat" style={{ fontSize:13, color:"var(--text-3)", animation:"spin .7s linear infinite" }}/>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  {!edit ? (
                    <button className="btn-itm btn-itm-outline"
                      onClick={() => setEdit(true)}
                      title="Modifier l'emplacement de cet équipement">
                      <i className="bi bi-geo-alt me-1"/>
                      Emplacement
                    </button>
                  ) : (
                    <button className="btn-itm btn-itm-outline"
                      onClick={() => setEdit(false)}>
                      <i className="bi bi-x me-1"/>Annuler
                    </button>
                  )}
                </div>
              </div>

              {/* Formulaire d'édition ou vue en lecture */}
              {edit ? (
                <div>
                  {/* Seul l'emplacement est modifiable — les données techniques viennent d'Atera */}
                  <div style={{ background:"rgba(230,51,41,.04)", border:"1px solid rgba(230,51,41,.15)", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#6B7280", display:"flex", alignItems:"center", gap:8 }}>
                    <i className="bi bi-info-circle" style={{ color:"#E63329", flexShrink:0 }}/>
                    Les données techniques (hostname, IP, RAM…) sont synchronisées depuis Atera et ne peuvent pas être modifiées ici.
                  </div>
                  <div className="form-group">
                    <label className="itm-label">Emplacement</label>
                    <input
                      className="itm-input"
                      value={form.location}
                      onChange={e => set("location", e.target.value)}
                      placeholder="Ex : Bureau 3, Salle serveur, Rack A2…"
                    />
                    <small style={{ fontSize:11, color:"#9CA3AF", marginTop:4, display:"block" }}>
                      Cette information est stockée dans Deskora uniquement, pas dans Atera.
                    </small>
                  </div>
                  <div style={{ display:"flex", justifyContent:"flex-end", marginTop:4 }}>
                    <button className="btn-itm btn-itm-primary" onClick={handleSave} disabled={saving}>
                      {saving ? "Enregistrement…" : "Enregistrer l'emplacement"}
                    </button>
                  </div>
                </div>
              ) : (
                /* Vue en lecture — grille de champs */
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 20px" }}>
                  {[
                    ["Fabricant",    asset.manufacturer],
                    ["Modèle",       asset.model],
                    ["N° de série",  asset.serialNumber],
                    ["OS",           asset.os],
                    ["CPU",          asset.cpu],
                    ["IP",           asset.ipAddress],
                    ["RAM",          asset.ramGb ? `${asset.ramGb} Go` : null],
                    ["Disque",       asset.diskTotalGb ? `${asset.diskTotalGb} Go` : null],
                    ["Emplacement",   asset.location || "(non défini — cliquer sur Emplacement pour renseigner)"],
                    ["Dernier user", asset.currentLoggedUser || asset.lastLoginUser],
                    ["Dernier contact", formatRelative(asset.lastSeenAt)],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                      <span style={{ color:"var(--text-3)" }}>{k}</span>
                      <span style={{ color:"var(--text-2)", textAlign:"right" }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Jauge disque */}
            {diskPct !== null && (
              <div className="itm-card anim-fade-up delay-1">
                <div className="card-title" style={{ marginBottom:12 }}>Stockage</div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--text-3)", marginBottom:6 }}>
                  <span>{asset.diskUsedGb} Go utilisés</span>
                  <span>{asset.diskTotalGb} Go total</span>
                </div>
                <div style={{ height:8, background:"var(--navy-soft)", borderRadius:99, overflow:"hidden" }}>
                  <div style={{
                    height:"100%", borderRadius:99, transition:"width .6s",
                    width:`${diskPct}%`,
                    background: diskPct > 85 ? "var(--danger)" : diskPct > 70 ? "var(--warning)" : "var(--accent)",
                  }}/>
                </div>
                <div style={{ fontSize:11, color:"var(--text-3)", textAlign:"right", marginTop:4 }}>{diskPct}%</div>
              </div>
            )}
          </div>

          {/* ── Colonne latérale ── */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Changer le statut */}
            <div className="itm-card anim-fade-up">
              <div className="card-title" style={{ marginBottom:12 }}>Statut</div>
              {[
                { value:"ONLINE",  label:"En ligne",    color:"var(--success)" },
                { value:"OFFLINE", label:"Hors ligne",  color:"var(--danger)"  },
              ].map(s => (
                <button key={s.value}
                  onClick={() => handleStatusChange(s.value)}
                  style={{
                    width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid",
                    borderColor: asset.status===s.value ? s.color : "var(--border)",
                    background:  asset.status===s.value ? s.color + "20" : "transparent",
                    color:       asset.status===s.value ? s.color : "var(--text-2)",
                    fontSize:13, cursor:"pointer", textAlign:"left", fontWeight: asset.status===s.value ? 600 : 400,
                    marginBottom:6, transition:"all .15s", display:"flex", alignItems:"center", gap:8,
                  }}>
                  {asset.status===s.value && <i className="bi bi-check2"/>}
                  {s.label}
                </button>
              ))}
            </div>

            {/* Informations Atera */}
            {(asset.ateraAgentId || asset.lastSyncedAt) && (
              <div className="itm-card anim-fade-up delay-1">
                <div className="card-title" style={{ marginBottom:12 }}>Synchronisation Atera</div>
                {[
                  ["Agent ID",     asset.ateraAgentId ? `#${asset.ateraAgentId}` : null],
                  ["Dernière sync", asset.lastSyncedAt ? new Date(asset.lastSyncedAt).toLocaleDateString("fr-FR") : null],
                ].filter(([,v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                    <span style={{ color:"var(--text-3)" }}>{k}</span>
                    <span style={{ color:"var(--text-2)" }}>{v}</span>
                  </div>
                ))}
              </div>
            )}

            {/* BIOS */}
            {asset.biosVersion && (
              <div className="itm-card anim-fade-up delay-2">
                <div className="card-title" style={{ marginBottom:12 }}>BIOS</div>
                {[
                  ["Fabricant",  asset.biosManufacturer],
                  ["Version",    asset.biosVersion],
                  ["Date",       asset.biosReleaseDate],
                ].filter(([,v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                    <span style={{ color:"var(--text-3)" }}>{k}</span>
                    <span style={{ color:"var(--text-2)" }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}